import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Money, TxList, METHOD_LABEL, METHOD_ACCOUNT_KIND } from "@/components/wallet/shared";
import CashAccountSelect, { useCashAccounts } from "@/components/wallet/CashAccountSelect";
import UnitsChips from "@/components/wallet/UnitsChips";
import { useWalletPackages } from "@/components/wallet/PackagesDialog";

function AssignPackage({ owner, onDone }) {
  const { data: pkgs = [] } = useWalletPackages();
  const [pid, setPid] = useState("");
  const active = pkgs.filter((p) => p.active);
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/wallet/packages/${pid}/assign`, { owner_type: owner.owner_type, owner_id: owner.owner_id })).data,
    onSuccess: () => { toast.success("Pacchetto assegnato"); setPid(""); onDone(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  if (!active.length) return null;
  return (
    <div className="flex gap-2">
      <Select value={pid} onValueChange={setPid}>
        <SelectTrigger className="flex-1" data-testid="assign-package-select"><SelectValue placeholder="Assegna un pacchetto prepagato…"/></SelectTrigger>
        <SelectContent>{active.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.quantity} × {p.item_name} · € {p.price.toFixed(2)}</SelectItem>)}</SelectContent>
      </Select>
      <Button disabled={!pid || mut.isPending} onClick={() => mut.mutate()} className="bg-[#0F766E] hover:bg-[#115E59]" data-testid="assign-package-btn">Assegna</Button>
    </div>
  );
}

const EMPTY = { kind: "ricarica", amount: "", method: "bonifico", reference: "", description: "", account_id: "" };

function TxForm({ owner, onDone }) {
  const [f, setF] = useState(EMPTY);
  const { data: accounts = [] } = useCashAccounts();
  const { data: cfg } = useQuery({ queryKey: ["wallet-config"], queryFn: async () => (await api.get("/wallet/config")).data });
  const methods = Object.entries(METHOD_LABEL).filter(([k]) => !cfg || (cfg.enabled_methods || []).includes(k));
  useEffect(() => { if (methods.length && !methods.some(([k]) => k === f.method)) setF((p) => ({ ...p, method: methods[0][0] })); }, [cfg]); // eslint-disable-line react-hooks/exhaustive-deps
  const setMethod = (m) => {
    const acc = accounts.find((a) => a.kind === METHOD_ACCOUNT_KIND[m]);
    setF({ ...f, method: m, account_id: acc?.id || f.account_id });
  };
  const mut = useMutation({
    mutationFn: async () => (await api.post("/wallet/transactions", {
      owner_type: owner.owner_type, owner_id: owner.owner_id, kind: f.kind, amount: parseFloat(f.amount),
      method: f.kind === "ricarica" ? f.method : null, reference: f.reference || null,
      description: f.description || null, account_id: f.kind === "ricarica" ? (f.account_id || null) : null,
    })).data,
    onSuccess: () => { toast.success("Movimento registrato"); setF(EMPTY); onDone(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const kinds = [["ricarica", "Ricarica"], ["accredito", "Assegna credito"], ["addebito", "Addebito"]];
  return (
    <div className="p-3 rounded-lg bg-stone-50 border border-stone-200 space-y-3">
      <div className="flex gap-1.5 flex-wrap">
        {kinds.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setF({ ...f, kind: k })} data-testid={`wallet-kind-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f.kind === k ? "bg-[#0F766E] text-white border-[#0F766E]" : "bg-white border-stone-200"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Importo (€)</Label><Input type="number" min="0.01" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="mt-1.5" data-testid="wallet-amount"/></div>
        {f.kind === "ricarica" ? (
          <div>
            <Label className="text-xs">Metodo</Label>
            <Select value={f.method} onValueChange={setMethod}>
              <SelectTrigger className="mt-1.5" data-testid="wallet-method"><SelectValue/></SelectTrigger>
              <SelectContent>{methods.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ) : (
          <div><Label className="text-xs">Riferimento</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className="mt-1.5"/></div>
        )}
      </div>
      {f.kind === "ricarica" && (
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Riferimento (CRO, ID transazione)</Label><Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className="mt-1.5" data-testid="wallet-reference"/></div>
          <div><Label className="text-xs">Registra entrata in cassa</Label><CashAccountSelect value={f.account_id} onChange={(v) => setF({ ...f, account_id: v })} testId="wallet-account"/></div>
        </div>
      )}
      <div><Label className="text-xs">Descrizione</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1.5" placeholder={f.kind === "addebito" ? "Es. libri, materiale" : "Es. ricarica mensile"} data-testid="wallet-description"/></div>
      <Button className="w-full bg-[#0F766E] hover:bg-[#115E59]" disabled={!(parseFloat(f.amount) > 0) || mut.isPending} onClick={() => mut.mutate()} data-testid="wallet-tx-save">
        {mut.isPending ? "Salvataggio…" : "Registra movimento"}
      </Button>
    </div>
  );
}

export default function OwnerWalletDialog({ owner, onClose }) {
  const qc = useQueryClient();
  const key = ["wallet-owner", owner?.owner_type, owner?.owner_id];
  const { data } = useQuery({
    queryKey: key, enabled: !!owner,
    queryFn: async () => (await api.get(`/wallet/owners/${owner.owner_type}/${owner.owner_id}`)).data,
  });
  const refresh = () => ["wallet-owners", "wallet-summary", "wallet-owner"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/wallet/transactions/${id}`)).data,
    onSuccess: () => { toast.success("Movimento eliminato"); refresh(); },
  });
  return (
    <Dialog open={!!owner} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="wallet-owner-dialog">
        <DialogHeader><DialogTitle>Wallet · {owner?.name}</DialogTitle></DialogHeader>
        <div className="rounded-xl p-4 bg-gradient-to-br from-[#0F766E] to-[#134E4A] text-white">
          <div className="text-[11px] uppercase tracking-widest opacity-75">Saldo disponibile</div>
          <div className="text-3xl font-semibold mt-1" data-testid="wallet-owner-balance"><Money value={data?.balance ?? owner?.balance ?? 0} className={(data?.balance ?? 0) < 0 ? "!text-red-200" : ""}/></div>
        </div>
        <UnitsChips units={data?.units}/>
        {owner && <AssignPackage owner={owner} onDone={refresh}/>}
        {owner && <TxForm owner={owner} onDone={refresh}/>}
        <div>
          <div className="text-sm font-medium mb-1">Movimenti</div>
          <TxList transactions={data?.transactions || []} onDelete={(t) => { if (window.confirm("Eliminare il movimento? Verrà rimossa anche l'eventuale entrata in cassa.")) del.mutate(t.id); }}/>
        </div>
      </DialogContent>
    </Dialog>
  );
}
